/**
 * Intent Intelligence — SLICE 3: LLM inference layer.
 *
 * This is the ONLY layer that calls the model. The deterministic 6-tier
 * suggester (`suggestProjectIntent`) and `nextBestInput` cover everything
 * we can derive without a model; this module fills the remaining gaps by
 * reading the assembled site context (SED prose, authored-context claims,
 * tagged documents) and asking Gemini to INFER the still-undecided
 * Project-Intent fields — never to overwrite a field the user owns.
 *
 * REUSE-first, ADDITIVE:
 *   - `runGeminiJSON` + `validateAgainstSchema` (responseSchemaValidator) for
 *     the structured-JSON round trip — same boundary contract as cafService /
 *     reportPrompts. The schema is JSON-mode-enforced AND re-validated by hand
 *     (belt-and-braces: Gemini's JSON-mode is not a hard guarantee).
 *   - `buildSedSectionForPrompt` (providerPrecedencePrompt) frames the SED
 *     block exactly as the report prompts do (precedence rule before the dump).
 *   - PII redaction (`redactPii`) + prompt-injection sanitisation are applied
 *     to EVERY user/AI-authored text fragment before it enters the prompt.
 *
 * GUARDRAILS (HARD):
 *   - No hallucinated fields: `INFERRED_INTENT_SCHEMA` declares an EXPLICIT
 *     enum array on every constrained field; `validateAgainstSchema` rejects
 *     any off-enum string. Numeric fields are clamped to the form's bounds
 *     (0.1–10000 MW) and DROPPED when out of range — never silently coerced.
 *   - USER ALWAYS WINS: a field whose `partialIntent.fieldSource[field]` is
 *     `'user'` / `'accepted'` / `'manual'` is removed from the schema sent to
 *     the model AND filtered from the result, so the model is never even
 *     asked to re-decide a value the user authored.
 *   - Tests MOCK `runGeminiJSON` — this module makes no real API call under test.
 *
 * Each inferred field is returned as an `IntentSuggestion<T>` with
 * `source: 'llm-inferred'` and the model-reported (clamped) confidence, so it
 * unions cleanly with the deterministic suggester's output in SLICE 4.
 */

import { Type, type Schema } from './geminiClient';
import { validateAgainstSchema } from './responseSchemaValidator';
import { buildSedSectionForPrompt } from '../lib/audit/providerPrecedencePrompt';
import { redactPii } from '../lib/llm/redactPii';
import type { ProjectIntent } from '../types/siteRag';
import type {
  IntentSuggestion,
  WorkloadProfileId,
  NuclearInterest,
  Priority,
  CoolingStrategy,
  BopHeatRejectionId,
  CoolingGenerationId,
  TimelineBucketId,
} from '../lib/suggestProjectIntent';

// ─────────────────────────────────────────────────────────────────────────────
// Field enums — single source of truth for both the Gemini Schema (explicit
// enum arrays) and the runtime guards. Mirroring the literal unions in
// types/siteRag.ts (ProjectIntent) and lib/suggestProjectIntent.ts keeps the
// off-enum rejection falsifiable rather than tautological.
// ─────────────────────────────────────────────────────────────────────────────

const WORKLOAD_PROFILE_ENUM: readonly WorkloadProfileId[] = [
  'ai-training', 'ai-inference', 'hpc', 'enterprise', 'colocation', 'edge', 'mixed',
] as const;
const COOLING_STRATEGY_ENUM: readonly CoolingStrategy[] = ['air', 'liquid', 'hybrid'] as const;
const BOP_HEAT_REJECTION_ENUM: readonly BopHeatRejectionId[] = [
  'dry-cooler', 'wet-cooling-tower', 'hybrid', 'once-through', 'absorption-cchp', 'free-cooling',
] as const;
// #157 — cooling-GENERATION MACHINE, separate from the IT-side coolingStrategy
// AND the cold-end sink (bopHeatRejection). Inferred + stored independently.
const COOLING_GENERATION_ENUM: readonly CoolingGenerationId[] = [
  'absorption-thermal', 'steam-driven-mechanical', 'vapour-compression-electric',
] as const;
const NUCLEAR_INTEREST_ENUM: readonly NuclearInterest[] = ['primary', 'considering', 'no'] as const;
const PRIORITY_ENUM: readonly Priority[] = ['cost', 'speed', 'sustainability', 'resilience'] as const;
const TIMELINE_ENUM: readonly TimelineBucketId[] = ['under-2yr', '2-5yr', 'over-5yr'] as const;
const CARBON_COMMITMENT_ENUM = [
  'none', 're100', '24-7-cfe', 'net-zero-2030', 'net-zero-2050', 'custom',
] as const;
const DC_TIER_ENUM = ['I', 'II', 'III', 'IV'] as const;
const SPONSOR_TYPE_ENUM = [
  'hyperscaler', 'ipp', 'sovereign', 'enterprise', 'consortium', 'unknown',
] as const;

/** Form bounds for the exact-MW figure — out-of-range numerics are DROPPED. */
export const INFER_POWER_MIN_MW = 0.1;
export const INFER_POWER_MAX_MW = 10000;
/** PUE physical bound — PUE < 1.0 is impossible (facility ≥ IT). Upper bound
 *  is generous; anything beyond is a model error and gets dropped. */
const INFER_PUE_MIN = 1.0;
const INFER_PUE_MAX = 4.0;

/**
 * The Project-Intent fields this LLM layer is permitted to infer. A subset of
 * `ProjectIntent` — the decision-shaping fields the deterministic suggester
 * already understands, plus the strategic enums (carbon / tier / sponsor) the
 * model can read from the deal narrative. Phasing / customWeights / free-text
 * fields are deliberately out of scope.
 */
export type InferableIntentField =
  | 'workloadProfile'
  | 'coolingStrategy'
  | 'bopHeatRejection'
  | 'coolingGeneration'
  | 'nuclearInterest'
  | 'priority'
  | 'timeline'
  | 'carbonCommitment'
  | 'datacenterTierTarget'
  | 'siteSponsorType'
  | 'powerRequirementMW'
  | 'pueValue';

/**
 * Inference result keyed by inferable field. Each present field carries an
 * `IntentSuggestion` with `source: 'llm-inferred'`. Absent keys mean the model
 * had no confident inference (or the field was user-authored / off-enum /
 * out-of-range and was rejected). `siteSponsorType` projects onto
 * `ProjectIntent.siteSponsor.type` at the merge layer.
 */
export type SuggestedFields = Partial<{
  workloadProfile: IntentSuggestion<WorkloadProfileId>;
  coolingStrategy: IntentSuggestion<CoolingStrategy>;
  bopHeatRejection: IntentSuggestion<BopHeatRejectionId>;
  coolingGeneration: IntentSuggestion<CoolingGenerationId>;
  nuclearInterest: IntentSuggestion<NuclearInterest>;
  priority: IntentSuggestion<Priority>;
  timeline: IntentSuggestion<TimelineBucketId>;
  carbonCommitment: IntentSuggestion<(typeof CARBON_COMMITMENT_ENUM)[number]>;
  datacenterTierTarget: IntentSuggestion<(typeof DC_TIER_ENUM)[number]>;
  siteSponsorType: IntentSuggestion<(typeof SPONSOR_TYPE_ENUM)[number]>;
  powerRequirementMW: IntentSuggestion<number>;
  pueValue: IntentSuggestion<number>;
}>;

/** Provenance tiers that mean the USER owns the field — never re-infer. */
const USER_AUTHORED_SOURCES: ReadonlySet<string> = new Set([
  'user',
  'accepted',
  'manual',
  // Explicit user-declared unknown ('Not sure yet' / draft-only creation) is a
  // deliberate operator state — never overwrite or trigger LLM inference for it.
  'user-unknown',
]);

/** True when `field` (or one of its aliases) is user-authored. The MW field is
 *  guarded under both `powerRequirementMW` and `powerRequirement`. */
function isUserAuthored(
  intent: Pick<ProjectIntent, 'fieldSource'> | undefined,
  ...fields: string[]
): boolean {
  const src = intent?.fieldSource;
  if (!src) return false;
  return fields.some((f) => {
    const v = (src as Record<string, string | undefined>)[f];
    return typeof v === 'string' && USER_AUTHORED_SOURCES.has(v);
  });
}

/** True when `intent` already carries a concrete (non-sentinel) value for the
 *  field — we only infer fields the user has NOT yet decided. */
function isAlreadyDecided(intent: ProjectIntent | undefined, field: InferableIntentField): boolean {
  if (!intent) return false;
  switch (field) {
    case 'workloadProfile':
      return intent.workloadProfile != null;
    case 'coolingStrategy':
      return intent.coolingStrategy != null && intent.coolingStrategy !== 'undecided';
    case 'bopHeatRejection':
      return intent.bopHeatRejection != null && intent.bopHeatRejection !== 'unknown';
    case 'coolingGeneration': {
      // #157 — separate from coolingStrategy AND bopHeatRejection. Duck-typed
      // read (the field is additive/forward-compatible). 'unknown' is the
      // not-yet-decided sentinel and remains eligible for inference.
      const cg = (intent as { coolingGeneration?: unknown }).coolingGeneration;
      return typeof cg === 'string' && cg !== 'unknown';
    }
    case 'nuclearInterest':
      return intent.nuclearInterest != null && intent.nuclearInterest !== 'undecided';
    case 'priority':
      return intent.priority != null && intent.priority !== 'undecided';
    case 'timeline':
      return (
        (typeof intent.inServiceDate === 'string' && intent.inServiceDate.length > 0) ||
        (intent.timeline != null && intent.timeline !== 'undecided')
      );
    case 'carbonCommitment':
      return intent.carbonCommitment != null;
    case 'datacenterTierTarget':
      return intent.datacenterTierTarget != null;
    case 'siteSponsorType':
      return intent.siteSponsor?.type != null && intent.siteSponsor.type !== 'unknown';
    case 'powerRequirementMW':
      return (
        (typeof intent.powerRequirementMW === 'number' && intent.powerRequirementMW > 0) ||
        (intent.powerRequirement != null && intent.powerRequirement !== 'undecided')
      );
    case 'pueValue':
      return typeof intent.pueValue === 'number' && intent.pueValue > 0;
    default: {
      const _exhaustive: never = field;
      void _exhaustive;
      return false;
    }
  }
}

/** Fields the user owns / has decided are EXCLUDED — never re-inferred. */
export function eligibleFields(partialIntent: ProjectIntent | undefined): InferableIntentField[] {
  const all: InferableIntentField[] = [
    'workloadProfile', 'coolingStrategy', 'bopHeatRejection', 'coolingGeneration',
    'nuclearInterest', 'priority', 'timeline', 'carbonCommitment', 'datacenterTierTarget',
    'siteSponsorType', 'powerRequirementMW', 'pueValue',
  ];
  return all.filter((field) => {
    if (isAlreadyDecided(partialIntent, field)) return false;
    // USER ALWAYS WINS — provenance-gated. MW + sponsor have alias keys.
    if (field === 'powerRequirementMW') {
      return !isUserAuthored(partialIntent, 'powerRequirementMW', 'powerRequirement');
    }
    if (field === 'siteSponsorType') {
      return !isUserAuthored(partialIntent, 'siteSponsorType', 'siteSponsor');
    }
    return !isUserAuthored(partialIntent, field);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Schema construction — one node per eligible field. Each constrained string
// node carries the EXPLICIT enum array so off-enum values are rejected both by
// Gemini's JSON-mode AND `validateAgainstSchema`. Every value node is paired
// with a `*Confidence` (0..100) and `*Rationale` so the union layer can rank.
// ─────────────────────────────────────────────────────────────────────────────

/** A `{ value, confidence, rationale }` triple, with an enum on `value`. */
function enumFieldSchema(enumValues: readonly string[]): Schema {
  return {
    type: Type.OBJECT,
    properties: {
      value: { type: Type.STRING, enum: enumValues as string[] },
      confidence: { type: Type.NUMBER },
      rationale: { type: Type.STRING },
    },
    required: ['value', 'confidence', 'rationale'],
    nullable: true,
  } as Schema;
}

function numberFieldSchema(): Schema {
  return {
    type: Type.OBJECT,
    properties: {
      value: { type: Type.NUMBER },
      confidence: { type: Type.NUMBER },
      rationale: { type: Type.STRING },
    },
    required: ['value', 'confidence', 'rationale'],
    nullable: true,
  } as Schema;
}

const FIELD_ENUM: Partial<Record<InferableIntentField, readonly string[]>> = {
  workloadProfile: WORKLOAD_PROFILE_ENUM,
  coolingStrategy: COOLING_STRATEGY_ENUM,
  bopHeatRejection: BOP_HEAT_REJECTION_ENUM,
  coolingGeneration: COOLING_GENERATION_ENUM,
  nuclearInterest: NUCLEAR_INTEREST_ENUM,
  priority: PRIORITY_ENUM,
  timeline: TIMELINE_ENUM,
  carbonCommitment: CARBON_COMMITMENT_ENUM,
  datacenterTierTarget: DC_TIER_ENUM,
  siteSponsorType: SPONSOR_TYPE_ENUM,
};

const NUMERIC_FIELDS: ReadonlySet<InferableIntentField> = new Set([
  'powerRequirementMW',
  'pueValue',
]);

/**
 * Build the `INFERRED_INTENT_SCHEMA` for the given eligible field set. The
 * exported constant is the FULL schema (all fields) for callers that want the
 * shape statically; the runtime path uses the per-request subset so the model
 * is never asked about a field the user already owns.
 */
export function buildInferredIntentSchema(fields: readonly InferableIntentField[]): Schema {
  const properties: Record<string, Schema> = {};
  for (const field of fields) {
    const enumValues = FIELD_ENUM[field];
    if (enumValues) {
      properties[field] = enumFieldSchema(enumValues);
    } else if (NUMERIC_FIELDS.has(field)) {
      properties[field] = numberFieldSchema();
    }
  }
  return {
    type: Type.OBJECT,
    properties,
    // No `required` — the model omits (or nulls) any field it can't infer.
  } as Schema;
}

/**
 * The full inference schema (every inferable field). Exported for static
 * consumers / tests. The live inference path narrows this to the eligible
 * subset via `buildInferredIntentSchema`.
 */
export const INFERRED_INTENT_SCHEMA: Schema = buildInferredIntentSchema([
  'workloadProfile', 'coolingStrategy', 'bopHeatRejection', 'coolingGeneration',
  'nuclearInterest', 'priority', 'timeline', 'carbonCommitment', 'datacenterTierTarget',
  'siteSponsorType', 'powerRequirementMW', 'pueValue',
]);

// ─────────────────────────────────────────────────────────────────────────────
// Prompt-input hardening — strip prompt-injection vectors + redact PII from
// every user/AI text fragment before it enters the prompt.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sanitise a user/AI-authored string for prompt embedding: collapse newlines,
 * strip control chars, cap length, then PII-redact. Mirrors the geminiService
 * `sanitizePromptInput` contract (kept local — that copy is not exported) and
 * layers `redactPii` on top (R-131-LLM-D).
 */
export function sanitizeForPrompt(input: string | undefined, maxLen = 4000): string {
  if (!input) return '';
  const stripped = input
    .replace(/[\r\n]+/g, ' ') // collapse newlines (injection vector)
    .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '') // strip control chars (mirror geminiService)
    .trim()
    .slice(0, maxLen);
  return redactPii(stripped).text;
}

/** A claim fragment the caller passes in for inference context. */
export interface InferenceClaim {
  claim: string;
  claimedValue?: string | number | boolean;
}

/** A tagged document summary the caller passes in for inference context. */
export interface InferenceDoc {
  fileName: string;
  summary?: string;
  tags?: string[];
}

export interface BuildIntentInferenceContextInput {
  /** Compiled SED text (authoritative evidence). Framed via `buildSedSectionForPrompt`. */
  sed?: string;
  /** Authored site-context prose (the user's free-text deal narrative). */
  siteContextProse?: string;
  /** Extracted/authored claims. */
  claims?: InferenceClaim[];
  /** Tagged document summaries (deal packets / sales decks / briefs). */
  docs?: InferenceDoc[];
  /** The current partial intent — its decided fields are described so the model
   *  knows what NOT to contradict (it is told to infer ONLY the listed gaps). */
  partialIntent?: ProjectIntent;
}

/**
 * Assemble the inference prompt context. Mirrors
 * `reportPrompts.buildFeasibilityContext`'s "framed sections" shape and reuses
 * `buildSedSectionForPrompt` for the SED block. All user/AI text is sanitised +
 * PII-redacted. Pure + deterministic for a given input (no store / network).
 */
export function buildIntentInferenceContext(
  input: BuildIntentInferenceContextInput,
  fields: readonly InferableIntentField[],
): string {
  const parts: string[] = [];

  parts.push(
    'You are a data-center / power infrastructure analyst. From the evidence ' +
      'below, INFER the project-intent fields listed in TARGET FIELDS. Only ' +
      'return a field when the evidence supports a defensible inference; OMIT ' +
      '(or set null) any field you cannot ground. Do NOT invent values. For ' +
      'each returned field give an integer confidence 0–100 and a one-sentence ' +
      'rationale citing the evidence. Use ONLY the allowed enum values.',
  );

  parts.push(`\nTARGET FIELDS (infer ONLY these): ${fields.join(', ')}.`);

  // #157 — disambiguate the three cooling fields so the model does not conflate
  // the GENERATION machine with the IT-side strategy or the cold-end sink.
  if (fields.includes('coolingGeneration')) {
    parts.push(
      '\nNOTE on coolingGeneration: this is the cooling-GENERATION MACHINE — ' +
        'how the chilling effect is PRODUCED, NOT how heat is rejected. Choose ' +
        "'absorption-thermal' (absorption / CCHP chiller driven by on-site " +
        "thermal heat), 'steam-driven-mechanical' (steam-turbine-driven chiller), " +
        "or 'vapour-compression-electric' (conventional electric chiller). Do " +
        'NOT answer with a heat-rejection sink (dry cooler / wet tower) — that is ' +
        'the separate bopHeatRejection field.',
    );
  }

  // Describe what's already decided so the model anchors, not contradicts.
  const pi = input.partialIntent;
  if (pi) {
    const decided: string[] = [];
    if (pi.workloadProfile) decided.push(`workload=${pi.workloadProfile}`);
    if (pi.coolingStrategy && pi.coolingStrategy !== 'undecided') decided.push(`cooling=${pi.coolingStrategy}`);
    if (pi.nuclearInterest && pi.nuclearInterest !== 'undecided') decided.push(`nuclear=${pi.nuclearInterest}`);
    if (pi.priority && pi.priority !== 'undecided') decided.push(`priority=${pi.priority}`);
    if (typeof pi.powerRequirementMW === 'number' && pi.powerRequirementMW > 0) {
      decided.push(`powerMW=${pi.powerRequirementMW}`);
    }
    if (decided.length > 0) {
      parts.push(`\nALREADY DECIDED (do not contradict): ${decided.join(', ')}.`);
    }
  }

  const sedSection = buildSedSectionForPrompt(input.sed ? sanitizeForPrompt(input.sed, 12000) : undefined);
  if (sedSection) parts.push(sedSection);

  const prose = sanitizeForPrompt(input.siteContextProse);
  if (prose) parts.push(`\nSITE CONTEXT (author narrative):\n${prose}`);

  if (input.claims && input.claims.length > 0) {
    const lines = input.claims
      .slice(0, 40)
      .map((c) => {
        const claim = sanitizeForPrompt(c.claim, 300);
        const val = c.claimedValue != null ? ` (value: ${sanitizeForPrompt(String(c.claimedValue), 120)})` : '';
        return `- ${claim}${val}`;
      })
      .filter((l) => l.trim() !== '-');
    if (lines.length > 0) parts.push(`\nEXTRACTED CLAIMS:\n${lines.join('\n')}`);
  }

  if (input.docs && input.docs.length > 0) {
    const lines = input.docs
      .slice(0, 20)
      .map((d) => {
        const name = sanitizeForPrompt(d.fileName, 200);
        const summary = sanitizeForPrompt(d.summary, 600);
        const tags = (d.tags ?? []).map((t) => sanitizeForPrompt(t, 60)).filter(Boolean).join(', ');
        return `- ${name}${tags ? ` [${tags}]` : ''}${summary ? `: ${summary}` : ''}`;
      })
      .filter((l) => l.trim() !== '-');
    if (lines.length > 0) parts.push(`\nDOCUMENTS:\n${lines.join('\n')}`);
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Inference entry point.
// ─────────────────────────────────────────────────────────────────────────────

export interface InferIntentInput extends BuildIntentInferenceContextInput {
  /** Override the model (defaults to the runGeminiJSON default — Pro). */
  model?: string;
  /** Per-call timeout. Defaults to runGeminiJSON's 120s. */
  timeoutMs?: number;
}

/** Raw `{ value, confidence, rationale }` triple shape returned per field. */
interface RawField {
  value: unknown;
  confidence?: unknown;
  rationale?: unknown;
}

/** Clamp a model confidence to an integer 0..100; defaults to 50 when absent. */
function clampConfidence(c: unknown): number {
  const n = Number(c);
  if (!Number.isFinite(n)) return 50;
  return Math.round(Math.max(0, Math.min(100, n)));
}

/** Trim a rationale to ≤140 chars (matches IntentSuggestion contract). */
function clipRationale(r: unknown): string {
  const s = typeof r === 'string' ? r : '';
  return s.length <= 140 ? s : `${s.slice(0, 139)}…`;
}

/** Clamp an MW figure to the form bounds; null (DROP) when out of range. */
function clampMw(v: unknown): number | null {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < INFER_POWER_MIN_MW || n > INFER_POWER_MAX_MW) return null;
  return n;
}

/** Clamp a PUE figure to its physical bounds; null (DROP) when out of range. */
function clampPue(v: unknown): number | null {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < INFER_PUE_MIN || n > INFER_PUE_MAX) return null;
  return n;
}

/**
 * Infer the undecided Project-Intent fields from the assembled site context.
 *
 * Pipeline:
 *   1. Compute the eligible field set (drops decided + user-authored fields).
 *      Empty set → return `{}` WITHOUT calling the model (cost guard).
 *   2. Build the per-request schema (explicit enums) + the sanitised prompt.
 *   3. `runGeminiJSON` (the only network call — MOCKED in tests).
 *   4. `validateAgainstSchema` — reject the whole response if it violates the
 *      schema is too strict; instead we validate per-field and drop offenders.
 *   5. Clamp numerics to bounds (drop out-of-range); stamp `source:'llm-inferred'`.
 *
 * Never throws — any model / parse / validation failure yields `{}` (the
 * deterministic layers still cover the gaps). The caller decides what to do
 * with an empty result.
 */
export async function inferIntentFromContext(input: InferIntentInput): Promise<SuggestedFields> {
  const fields = eligibleFields(input.partialIntent);
  if (fields.length === 0) return {};

  const schema = buildInferredIntentSchema(fields);
  const contents = buildIntentInferenceContext(input, fields);

  let raw: Record<string, unknown>;
  try {
    // Dynamic import mirrors cafService — keeps the heavy SDK out of the
    // module graph until inference actually runs, and is the seam tests mock.
    const { runGeminiJSON } = await import('./geminiClient');
    raw = await runGeminiJSON<Record<string, unknown>>({
      ...(input.model ? { model: input.model } : {}),
      contents,
      schema,
      ...(input.timeoutMs ? { timeoutMs: input.timeoutMs } : {}),
    });
  } catch {
    // Network / timeout / invalid-JSON — degrade silently to no inference.
    return {};
  }

  if (!raw || typeof raw !== 'object') return {};

  const out: SuggestedFields = {};

  for (const field of fields) {
    const node = (raw as Record<string, RawField | null | undefined>)[field];
    if (node == null || typeof node !== 'object') continue;
    const confidence = clampConfidence(node.confidence);
    const rationale = clipRationale(node.rationale);

    if (NUMERIC_FIELDS.has(field)) {
      // Validate the node shape against the numeric sub-schema.
      const sub = numberFieldSchema();
      if (!validateAgainstSchema(node, sub).ok) continue;
      const clamped = field === 'powerRequirementMW' ? clampMw(node.value) : clampPue(node.value);
      if (clamped == null) continue; // out-of-range → DROP (GUARDRAILS)
      if (field === 'powerRequirementMW') {
        out.powerRequirementMW = { value: clamped, source: 'llm-inferred', rationale, confidence };
      } else {
        out.pueValue = { value: clamped, source: 'llm-inferred', rationale, confidence };
      }
      continue;
    }

    // Enum field — validate against the explicit-enum sub-schema. An off-enum
    // value fails `validateAgainstSchema` and is dropped (no hallucination).
    const enumValues = FIELD_ENUM[field];
    if (!enumValues) continue;
    const sub = enumFieldSchema(enumValues);
    if (!validateAgainstSchema(node, sub).ok) continue;
    const value = node.value as string;

    switch (field) {
      case 'workloadProfile':
        out.workloadProfile = { value: value as WorkloadProfileId, source: 'llm-inferred', rationale, confidence };
        break;
      case 'coolingStrategy':
        out.coolingStrategy = { value: value as CoolingStrategy, source: 'llm-inferred', rationale, confidence };
        break;
      case 'bopHeatRejection':
        out.bopHeatRejection = { value: value as BopHeatRejectionId, source: 'llm-inferred', rationale, confidence };
        break;
      case 'coolingGeneration':
        out.coolingGeneration = { value: value as CoolingGenerationId, source: 'llm-inferred', rationale, confidence };
        break;
      case 'nuclearInterest':
        out.nuclearInterest = { value: value as NuclearInterest, source: 'llm-inferred', rationale, confidence };
        break;
      case 'priority':
        out.priority = { value: value as Priority, source: 'llm-inferred', rationale, confidence };
        break;
      case 'timeline':
        out.timeline = { value: value as TimelineBucketId, source: 'llm-inferred', rationale, confidence };
        break;
      case 'carbonCommitment':
        out.carbonCommitment = {
          value: value as (typeof CARBON_COMMITMENT_ENUM)[number],
          source: 'llm-inferred', rationale, confidence,
        };
        break;
      case 'datacenterTierTarget':
        out.datacenterTierTarget = {
          value: value as (typeof DC_TIER_ENUM)[number],
          source: 'llm-inferred', rationale, confidence,
        };
        break;
      case 'siteSponsorType':
        out.siteSponsorType = {
          value: value as (typeof SPONSOR_TYPE_ENUM)[number],
          source: 'llm-inferred', rationale, confidence,
        };
        break;
      default:
        break;
    }
  }

  return out;
}
